import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { listPrograms, listOrders } from "@/lib/admin.functions";
import { useRoleGuard } from "@/hooks/use-role-guard";

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Admin overview - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminOverview,
});

function AdminOverview() {
  const { status: roleStatus } = useRoleGuard(["admin", "super_admin"]);
  const runList = useServerFn(listPrograms);
  const runOrders = useServerFn(listOrders);
  const [counts, setCounts] = useState<{ programsTotal: number; programsActive: number } | null>(
    null,
  );
  const [overdue, setOverdue] = useState<number | null>(null);

  useEffect(() => {
    runList()
      .then(({ programs }) => {
        setCounts({
          programsTotal: programs.length,
          programsActive: programs.filter((p) => p.active).length,
        });
      })
      .catch(() => setCounts({ programsTotal: 0, programsActive: 0 }));
    runOrders()
      .then(({ orders }) => {
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        setOverdue(
          orders.filter(
            (o) => o.fulfillment_status !== "completed" && o.created_at && new Date(o.created_at).getTime() < cutoff,
          ).length,
        );
      })
      .catch(() => setOverdue(0));
  }, [runList, runOrders]);

  if (roleStatus !== "ok") return null;
  return (
    <>
      <div className="mb-8">
        <p className="font-script text-accent text-[22px] leading-none">super admin</p>
        <h1 className="font-display text-[28px] font-semibold mt-2">Overview</h1>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Programs" value={counts?.programsTotal ?? "-"} />
        <StatCard label="Active programs" value={counts?.programsActive ?? "-"} />
        <StatCard
          label="Overdue orders"
          value={overdue ?? "-"}
          valueClassName={overdue && overdue > 0 ? "text-red" : undefined}
        />
        <StatCard label="Analyses" value="-" hint="coming soon" />
      </div>
      <SubscriptionsCard />
      <ImportGrantsCard />
    </>
  );
}

function ImportGrantsCard() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!rows.length) {
        toast.error("No rows found in file.");
        return;
      }
      const { data, error } = await supabase.functions.invoke("import-grants", {
        body: { rows, source_name: file.name },
      });
      if (error) {
        const msg =
          (data as { message?: string } | null)?.message || error.message || "Import failed.";
        toast.error(msg);
        return;
      }
      const d = data as {
        ok?: boolean;
        inserted?: number;
        skipped?: number;
        truncated?: boolean;
        message?: string;
      };
      if (d?.ok) {
        const skipped = d.skipped ?? 0;
        let msg = `Imported ${d.inserted ?? 0} grants to review`;
        if (skipped > 0) msg += ` (${skipped} rows skipped)`;
        if (d.truncated) msg += ". Only the first 150 rows were processed.";
        toast.success(msg);
      } else {
        toast.error(d?.message || "Import failed.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="mt-8 bg-surface border border-line rounded-2xl p-6">
      <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">Grants</div>
      <h2 className="mt-1 font-display text-[20px] font-semibold">Import grants</h2>
      <p className="mt-1 text-[13px] text-ink-secondary">
        Upload any Excel/CSV of grants — even messy. AI maps the columns and adds them to the
        review queue (pending approval).
      </p>
      <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={onFile}
          disabled={busy}
          className="block text-[13px] text-ink file:mr-3 file:rounded-full file:border-0 file:bg-accent file:px-4 file:py-2 file:text-[13px] file:font-bold file:text-accent-ink hover:file:bg-accent-press disabled:opacity-60"
        />
        {busy && <span className="text-[13px] text-ink-secondary">Importing…</span>}
      </div>
      <Link
        to="/admin/grants"
        className="mt-4 inline-block text-[13px] text-accent hover:underline"
      >
        Review imported grants →
      </Link>
    </div>
  );
}

function SubscriptionsCard() {
  return (
    <div className="mt-8 bg-surface border border-line rounded-2xl p-6">
      <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">Subscriptions</div>
      <h2 className="mt-1 font-display text-[20px] font-semibold">Manage Pro users</h2>
      <p className="mt-1 text-[13px] text-ink-secondary">
        Grant, extend or revoke extension Pro plans. See engagement per user.
      </p>
      <Link
        to="/admin/subscriptions"
        className="mt-4 inline-block text-[13px] text-accent hover:underline"
      >
        Open subscriptions module →
      </Link>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  valueClassName,
}: {
  label: string;
  value: number | string;
  hint?: string;
  valueClassName?: string;
}) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-5">
      <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">{label}</div>
      <div className={`mt-2 font-display text-[28px] font-semibold ${valueClassName ?? ""}`}>{value}</div>
      {hint && <div className="mt-1 text-[12px] text-ink-tertiary">{hint}</div>}
    </div>
  );
}
